import { Tag } from 'antd';
import React, { useLayoutEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import PlusSvg from '../../../assets/icons/PlusSvg';
import theme from '../../../assets/theme';
import { useCroppedImage } from '../../../hooks/useCroppedImage';
import { useTextFile } from '../../../hooks/useGameMediaBackgroundJSONFile';
import { useGameMediaFile } from '../../../hooks/useGameMediaFile';
import useSvgImage from '../../../hooks/useSvgImage';
import { CropConfig } from '../../../utils/imageCropUtils';
import { getExtensionFromMimeType } from '../../../utils/mimetype';
import { IconWrapper } from '../../_atoms/IconWrapper';
import ImageSkeleton from '../../_atoms/ImageSkeleton';
import { MarkdownSlateViewer } from '../../_organisms/DesignerTabPane/AgentTab/MarkdownSlateViewer/MarkdownSlateViewer';

/**
 * `useLayoutEffect` that degrades to a no-op on the server, where there is no
 * paint to get ahead of and React warns about the layout variant.
 */
const useBeforePaint = typeof window === 'undefined' ? () => undefined : useLayoutEffect;

/**
 * An `<img>` that shows the transparent slide skeleton until it has actually
 * painted. Covers both waits: `pending` for the upstream URL resolving, and the
 * browser's own download/decode of the file — the old spinner only covered the
 * first, so images still popped in from nothing.
 *
 * The skeleton fills the whole slot regardless of the image's aspect ratio, so
 * `object-fit: contain` images reserve their full box rather than a letterbox.
 */
export const SkeletonImage = ({
  pending,
  src,
  alt,
  objectFit,
  brightness,
  clickable,
  radius,
  aspectRatio,
  style,
  onClick,
  onLoad,
}: {
  pending?: boolean;
  src?: string;
  alt?: string;
  objectFit?: string;
  brightness?: number | null;
  clickable?: boolean;
  radius?: number | string;
  /**
   * Box to reserve while the skeleton is up, e.g. `'16 / 9'`. Only needed when
   * the parent doesn't give the slot a height of its own — on a slide the
   * element's box already does.
   */
  aspectRatio?: string;
  style?: React.CSSProperties;
  onClick?: () => void;
  onLoad?: (e: React.SyntheticEvent<HTMLImageElement, Event>) => void;
}) => {
  const imgRef = useRef<HTMLImageElement>(null);
  const [painted, setPainted] = useState(false);
  // True when the image was already decoded the moment it mounted — a slide the
  // teacher has visited before. Those must appear instantly, so the fade is
  // switched off rather than played from opacity 0.
  const [fromCache, setFromCache] = useState(false);
  // On error we drop the skeleton too — a broken image should show as broken
  // rather than shimmer forever.
  const showSkeleton = !!pending || !src || !painted;

  // Layout effect, not `useEffect`: this has to settle before the browser
  // paints. Run after paint and a cached image still gets one frame at opacity
  // 0, which is enough to start the transition and fade something that was
  // never actually loading.
  useBeforePaint(() => {
    const img = imgRef.current;
    const cached = !!img?.complete && img.naturalWidth > 0;
    setPainted(cached);
    setFromCache(cached);
  }, [src]);

  return (
    <SkeletonImageSlot
      $painted={!showSkeleton}
      $instant={fromCache}
      $aspectRatio={showSkeleton ? aspectRatio : undefined}
    >
      <ImgStyled
        ref={imgRef}
        src={src}
        alt={alt || ''}
        $objectFit={objectFit}
        $brightness={brightness}
        $clickable={clickable}
        style={style}
        onClick={onClick}
        onLoad={e => {
          setPainted(true);
          onLoad?.(e);
        }}
        onError={() => setPainted(true)}
      />
      {showSkeleton && (
        <SkeletonOverlay>
          <ImageSkeleton radius={radius} framed={false} />
        </SkeletonOverlay>
      )}
    </SkeletonImageSlot>
  );
};

export const AgentImageRenderer = ({
  fileId,
  fileMimetype,
  onClickAddToSlide,
  objectFit,
  brightness,
  fileAltText,
  style,
}: {
  fileId: string;
  fileMimetype: string;
  onClickAddToSlide: () => void;
  objectFit?: string;
  brightness?: number | null;
  fileAltText?: string | null;
  style?: React.CSSProperties;
}) => {
  const { imageUrl, isLoading } = useCroppedImage({
    fileId,
    cropConfig: undefined,
    fileMimetype,
    isDiscoverTemplate: false,
    isAuthenticated: true,
    isOwner: true,
  });

  return (
    <div style={{ position: 'relative', padding: '0px 20px', marginBottom: 20 }}>
      <SkeletonImage
        pending={isLoading && !imageUrl}
        objectFit={objectFit}
        brightness={brightness}
        src={imageUrl}
        alt={fileAltText || ''}
        style={style}
        /* This panel sizes itself off the image, so reserve a box until it lands. */
        aspectRatio="16 / 9"
      />
      <ImageActionsContainer>
        <ImageActionButton
          onClick={() => {
            onClickAddToSlide();
          }}
        >
          <IconWrapper height="14px" width="14px">
            <PlusSvg />
          </IconWrapper>
          <span>Add to slide</span>
        </ImageActionButton>
        {/* <ImageActionButton $disabled={true}>
          <IconWrapper>
            <RefreshSvg />
          </IconWrapper>
          <span>Make drawing background</span>
        </ImageActionButton> */}
      </ImageActionsContainer>
    </div>
  );
};

const ImageActionsContainer = styled.div`
  position: absolute;
  height: 28px;
  bottom: -14px;
  right: 5px;
  display: grid;
  grid-template-columns: auto auto;
  align-items: center;
  /* gap: 3px; */
  border-radius: 8px;
  border: 1px solid #a8a8a8;
  background-color: ${theme.colors.greyLight};
  box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.1);
  padding: 3px 3px;
`;

const ImageActionButton = styled.div<{ $disabled?: boolean }>`
  display: grid;
  align-items: center;
  height: 100%;
  grid-template-columns: 16px 1fr;
  gap: 3px;
  border-radius: 5px;
  opacity: ${props => (props.$disabled ? 0.5 : 1)};
  cursor: ${props => (props.$disabled ? 'not-allowed' : 'pointer')};

  font-size: 10px;
  font-weight: bold;

  transition: all 0.2s ease;
  padding: 0px 5px;

  &:hover {
    background-color: ${theme.colors.grey};
  }

  ${({ $disabled }) =>
    $disabled &&
    css`
      opacity: 0.5;
      cursor: not-allowed;
      &:hover {
        background-color: transparent;
      }
    `}
`;

const CroppedGameImageRenderer = ({
  fileId,
  fileAltText,
  cropConfig,
  fileMimetype,
  isDiscoverTemplate,
  objectFit,
  onImageClicked,
  style,
  brightness,
  isAuthenticated,
  isOwner,
  onLoad,
  skeletonRadius,
}: {
  fileId: string;
  fileMimetype: string;
  fileAltText?: string | null;
  cropConfig?: CropConfig | null;
  isDiscoverTemplate: boolean;
  objectFit?: string;
  brightness?: number | null;
  onImageClicked?: (url: string) => void;
  style?: React.CSSProperties;
  isAuthenticated: boolean;
  isOwner: boolean;
  onLoad?: (e: React.SyntheticEvent<HTMLImageElement, Event>) => void;
  skeletonRadius?: number | string;
}) => {
  const { imageUrl, isLoading } = useCroppedImage({
    fileId,
    cropConfig,
    fileMimetype,
    isDiscoverTemplate,
    isAuthenticated,
    isOwner,
  });

  return (
    <SkeletonImage
      pending={isLoading && !imageUrl}
      onLoad={onLoad}
      objectFit={objectFit}
      brightness={brightness}
      src={imageUrl}
      alt={fileAltText || ''}
      onClick={() => onImageClicked && imageUrl && onImageClicked(imageUrl)}
      clickable={!!onImageClicked}
      radius={skeletonRadius}
      style={style}
    />
  );
};

export const TextFileRenderer = ({ fileId }: { fileId: string }) => {
  const { data } = useTextFile(fileId);

  return (
    <div
      style={{
        height: '100%',
        width: '100%',
        overflow: 'hidden',
        padding: '4px 6px',
        backgroundColor: theme.colors.paleBeige,
      }}
    >
      <p style={{ fontSize: 9, textAlign: 'left', width: '100%' }}>{data}</p>
      <BottomBlur />
    </div>
  );
};

export const OtherFileRenderer = ({
  fileId,
  fileMimetype,
}: {
  fileId: string;
  fileMimetype: string;
}) => {
  return (
    <div
      style={{
        height: '100%',
        width: '100%',
        padding: '4px 6px',
        backgroundColor: theme.colors.paleBeige,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'start',
        gap: 4,
        justifyContent: 'space-between',
        fontSize: 9,
      }}
    >
      <p
        style={{
          display: '-webkit-box',
          overflow: 'hidden',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflowWrap: 'anywhere',
          wordBreak: 'break-all',
          textAlign: 'left',
        }}
      >
        {fileId}
      </p>
      <Tag>{getExtensionFromMimeType(fileMimetype)}</Tag>
    </div>
  );
};
export const TextFileRendererLargePreview = ({ fileId }: { fileId: string }) => {
  const { data } = useTextFile(fileId);

  return (
    <div
      style={{
        height: '100%',
        width: '100%',
        overflowY: 'auto',
        padding: '8px 10px',
      }}
    >
      <MarkdownSlateViewer content={data || ''} />
    </div>
  );
};

const BottomBlur = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to top, rgba(255, 255, 255, 1), transparent);
`;

const SvgFileRenderer = ({
  fileId,
  isAuthenticated,
  fileAltText,
  isOwner,
  skeletonRadius,
}: {
  fileId: string;
  isAuthenticated: boolean;
  fileAltText?: string | null;
  isOwner: boolean;
  skeletonRadius?: number | string;
}) => {
  const { svgText, isLoading } = useSvgImage({
    fileId,
    fileMimetype: 'image/svg+xml',
    isDiscoverTemplate: false,
    isAuthenticated: isAuthenticated,
    isOwner: isOwner,
    colors: [],
  });

  if (isLoading && !svgText) {
    return <ImageSkeleton radius={skeletonRadius} framed={false} />;
  }

  return (
    <SvgImg
      aria-label={fileAltText || ''}
      dangerouslySetInnerHTML={{ __html: svgText }}
    />
  );
};

const GifFileRenderer = ({
  fileId,
  isAuthenticated,
  objectFit,
  brightness,
  fileAltText,
  style,
  onLoad,
  skeletonRadius,
}: {
  fileId: string;
  isAuthenticated: boolean;
  objectFit?: string;
  brightness?: number | null;
  fileAltText?: string | null;
  style?: React.CSSProperties;
  onLoad?: (e: React.SyntheticEvent<HTMLImageElement, Event>) => void;
  skeletonRadius?: number | string;
}) => {
  const { data, isLoading } = useGameMediaFile(fileId, isAuthenticated);

  return (
    <SkeletonImage
      pending={isLoading && !data}
      objectFit={objectFit}
      brightness={brightness}
      src={data}
      alt={fileAltText || ''}
      clickable={false}
      radius={skeletonRadius}
      style={style}
      onLoad={onLoad}
    />
  );
};

const GameImageRenderer = ({
  fileId,
  fileAltText,
  cropConfig,
  fileMimetype,
  isDiscoverTemplate,
  objectFit,
  onImageClicked,
  style,
  brightness,
  isAuthenticated,
  isOwner,
  onLoad,
  skeletonRadius,
}: {
  fileId: string;
  fileMimetype: string;
  fileAltText?: string | null;
  cropConfig?: CropConfig | null;
  isDiscoverTemplate: boolean;
  objectFit?: string;
  brightness?: number | null;
  onImageClicked?: (url: string) => void;
  style?: React.CSSProperties;
  isAuthenticated: boolean;
  isOwner: boolean;
  onLoad?: (e: React.SyntheticEvent<HTMLImageElement, Event>) => void;
  /** Corner rounding for the loading skeleton — match the element's own. */
  skeletonRadius?: number | string;
}) => {
  // @depricated by new mediaUrl
  if (fileMimetype === 'image/gif') {
    return (
      <GifFileRenderer
        fileAltText={fileAltText}
        fileId={fileId}
        brightness={brightness}
        isAuthenticated={isAuthenticated}
        objectFit={objectFit}
        style={style}
        onLoad={onLoad}
        skeletonRadius={skeletonRadius}
      />
    );
  }

  if (fileId.includes('.svg')) {
    return (
      <SvgFileRenderer
        fileAltText={fileAltText}
        fileId={fileId}
        isAuthenticated={isAuthenticated}
        isOwner={isOwner}
        skeletonRadius={skeletonRadius}
      />
    );
  }

  return (
    <CroppedGameImageRenderer
      fileAltText={fileAltText}
      fileId={fileId}
      fileMimetype={fileMimetype}
      brightness={brightness}
      cropConfig={cropConfig}
      isDiscoverTemplate={isDiscoverTemplate}
      isAuthenticated={isAuthenticated}
      objectFit={objectFit}
      onImageClicked={onImageClicked}
      style={style}
      isOwner={isOwner}
      onLoad={onLoad}
      skeletonRadius={skeletonRadius}
    />
  );
};

export default GameImageRenderer;

const SkeletonImageSlot = styled.div<{
  $painted: boolean;
  $instant: boolean;
  $aspectRatio?: string;
}>`
  position: relative;
  width: 100%;
  height: 100%;
  ${({ $aspectRatio }) =>
    $aspectRatio &&
    css`
      aspect-ratio: ${$aspectRatio};
    `}

  /* Kept in the layout while loading so the box is reserved, but invisible —
     the wash is semi-transparent, so a half-decoded image would show through
     it otherwise. The fade is the handoff *from* the skeleton; an image that
     was already in cache never showed one, so it gets no transition at all. */
  > img {
    opacity: ${({ $painted }) => ($painted ? 1 : 0)};
    ${({ $instant }) =>
      !$instant &&
      css`
        transition: opacity 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
      `}
  }
`;

const SkeletonOverlay = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
`;

export const ImgStyled = styled.img<{
  $objectFit?: string;
  $clickable?: boolean;
  $brightness?: number | null;
}>`
  width: 100%;
  height: 100%;
  filter: ${({ $brightness }) =>
    /** 0.5 is no change in brightness, but needs to be translated into the value of 1 for CSS */

    $brightness && `brightness(${($brightness || 0.5) + 0.5})`};

  ${({ $clickable }) =>
    $clickable &&
    css`
      cursor: pointer;
    `}
  ${({ $objectFit = 'contain' }) =>
    $objectFit &&
    css`
      object-fit: ${$objectFit};
    `}
`;
const SvgImg = styled.div`
  width: 100%;
  height: 100%;
  svg {
    width: 100%;
    height: auto;
  }
`;
